/**
 * ExplorerTab — interactive Sigma.js graph explorer for the PDO.
 */
import { useCallback } from 'react';
import { Search, RotateCcw } from 'lucide-react';
import { useOntologyStore } from '@/stores/ontology';
import { OntologyGraph, ENTITY_TYPE_COLORS } from './OntologyGraph';
import { FilterPanel } from './FilterPanel';
import { NodeInspectorPanel } from './NodeInspectorPanel';

export function ExplorerTab() {
  const filteredGraph = useOntologyStore((s) => s.filteredGraph());
  const currentGraph  = useOntologyStore((s) => s.currentGraph());
  const graphLoading  = useOntologyStore((s) => s.graphLoading);
  const graphError    = useOntologyStore((s) => s.graphError);
  const focusedNodeId = useOntologyStore((s) => s.focusedNodeId);
  const filters       = useOntologyStore((s) => s.filters);
  const setFocusedNode = useOntologyStore((s) => s.setFocusedNode);
  const setFilters     = useOntologyStore((s) => s.setFilters);

  const handleNodeClick = useCallback((nodeId: string) => {
    setFocusedNode(nodeId);
  }, [setFocusedNode]);

  const handleNavigate = useCallback((nodeId: string) => {
    setFocusedNode(nodeId);
  }, [setFocusedNode]);

  if (graphLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-slate-500">
          <div className="w-8 h-8 border-2 border-brand-teal border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm">Loading ontology graph…</p>
        </div>
      </div>
    );
  }

  if (graphError) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-red-400">
          <p className="text-sm font-medium">Failed to load graph</p>
          <p className="text-xs mt-1 text-slate-500">{graphError}</p>
        </div>
      </div>
    );
  }

  if (!filteredGraph || filteredGraph.nodes.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-slate-500">
          <p className="text-sm font-medium">No concepts match the current filters</p>
          <button
            onClick={() => setFilters({ entityTypes: [], categories: [], relTypes: [] })}
            className="mt-2 text-xs text-brand-teal hover:text-white flex items-center gap-1 mx-auto"
          >
            <RotateCcw size={12} /> Reset filters
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* Filter sidebar */}
      {currentGraph && <FilterPanel graph={currentGraph} />}

      {/* Graph canvas */}
      <div className="flex-1 relative min-h-0 bg-slate-900">
        {/* Search bar overlay */}
        <div className="absolute top-3 left-3 right-3 z-10 flex items-center gap-2 pointer-events-none">
          <div className="relative pointer-events-auto flex-1 max-w-xs">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search concepts…"
              value={filters.searchQuery}
              onChange={(e) => setFilters({ searchQuery: e.target.value })}
              className="w-full pl-8 pr-3 py-1.5 bg-navy-900/90 backdrop-blur border border-navy-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-teal"
            />
          </div>

          {/* Legend */}
          <div className="pointer-events-none flex items-center gap-3 bg-navy-900/90 backdrop-blur border border-navy-700 rounded-lg px-3 py-1.5">
            {Object.entries(ENTITY_TYPE_COLORS)
              .filter(([k]) => k !== 'OTHER')
              .map(([type, color]) => (
                <div key={type} className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-[10px] text-slate-400">{type}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Stats bar */}
        <div className="absolute bottom-3 left-3 z-10 flex items-center gap-3 bg-navy-900/90 backdrop-blur border border-navy-700 rounded-lg px-3 py-1.5 text-[10px] font-mono text-slate-500">
          <span>{filteredGraph.nodes.length} concepts</span>
          <span className="text-navy-600">·</span>
          <span>{filteredGraph.edges.length} relations</span>
        </div>

        <OntologyGraph
          graph={filteredGraph}
          onNodeClick={handleNodeClick}
        />
      </div>

      {/* Node inspector */}
      {focusedNodeId && (
        <NodeInspectorPanel
          nodeId={focusedNodeId}
          onClose={() => setFocusedNode(null)}
          onNavigate={handleNavigate}
        />
      )}
    </div>
  );
}
